import React, { useState } from 'react';
import { Form, message } from 'antd';
import { useSpin } from '@/components/SpinContext.jsx';
import { useToken } from '@/common/helper';
import CommonDrawer from '@/components/CommonDrawer';
import { CreateCustomerCredential } from '@/apis/customerCredentials';
import type {
  CredentialAuthType,
  CredentialData,
} from '@/apis/customerCredentials';
import CredentialForm from './CredentialForm';
import type { CredentialFormValues } from './CredentialForm';

// The API function rejects with the axios response, so the server's message
// sits at `data.data.message`. One cast to the narrowest shape, since the
// rejection is typed `unknown`.
const errorMessage = (error: unknown, fallback: string): string =>
  (error as { data?: { data?: { message?: string } } })?.data?.data?.message ||
  fallback;

interface CreateCredentialDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Called once the credential is saved, with the stored record (never the
   * secret). The drawer does not close itself; the caller does. */
  onCreated: (credential: CredentialData | undefined) => void;
  /** Pre-selects the authentication type, for callers that already know it. */
  defaultAuthType?: CredentialAuthType;
}

/** The "New credential" side drawer: the form, the create call and its toasts.
 * Shared by the Settings > Credentials page and anywhere else a credential can
 * be added, so there is one create flow. */
const CreateCredentialDrawer: React.FC<CreateCredentialDrawerProps> = ({
  open,
  onClose,
  onCreated,
  defaultAuthType,
}) => {
  const { token } = useToken();
  const { showSpin, hideSpin } = useSpin();
  const [form] = Form.useForm<CredentialFormValues>();
  const [saving, setSaving] = useState(false);

  const handleCreate = (payload: CredentialFormValues): void => {
    setSaving(true);
    showSpin();
    CreateCustomerCredential(token, payload)
      .then((res) => {
        const data = res?.data?.data;
        if (data?.is_success) {
          message.success(data?.message || 'Credential created successfully!');
          onCreated(data.data);
        } else {
          message.error(data?.message || 'Failed to create credential.');
        }
      })
      .catch((error) => {
        console.error('Error creating credential:', error);
        message.error(errorMessage(error, 'Failed to create credential.'));
      })
      .finally(() => {
        setSaving(false);
        hideSpin();
      });
  };

  // Mounted only while open, so each drawer starts from a clean form and a
  // typed secret never outlives it in the DOM.
  if (!open) return null;

  return (
    <CommonDrawer open onClose={onClose}>
      <CredentialForm
        form={form}
        saving={saving}
        defaultAuthType={defaultAuthType}
        onCancel={onClose}
        onFinish={handleCreate}
      />
    </CommonDrawer>
  );
};

export default CreateCredentialDrawer;
